"use client";

import Image from "next/image";
import { useCart } from "../CartContext";
import {
  BookStencil,
  Canvas,
  Card,
  Tag,
  Typography,
} from "@cappelendamm/design-system/react";
import { Minus, Plus, Trash } from "iconoir-react";
import classNames from "classnames";
import { getCartNumberOfItems, isFreightItem } from "../selectors";
import { isItemDigital } from "@/products/selectors/isItemDigital";
import ProductLink from "@/products/components/ProductLink";
import { Price } from "@/products/components/Price";
import { getCartItemPrice } from "@/products/pricing";
import { usePreorderSkus } from "@/cart/hooks/usePreorderSkus";
import { formatPublicationDate } from "@/utils/formatDate";
import { useRef, useState } from "react";

export function CartItems({ className }: { className?: string }) {
  const {
    cart,
    increaseQuantity,
    decreaseQuantity,
    removeItem,
    setQuantity,
    isPending,
  } = useCart();
  const preorderSkus = usePreorderSkus(cart);

  if (getCartNumberOfItems(cart) === 0)
    return (
      <div className="mt-8 mb-auto text-center">
        <Typography as="h2" variant="title-lg">
          Handlekurven er tom
        </Typography>
        <p className="text-lg">Du har ingen produkter i handlekurven</p>
      </div>
    );

  const hasDigitalItems = cart?.items.some((item) => isItemDigital(item));
  const hasPreorderItems = preorderSkus.size > 0;
  const hasConflict = hasDigitalItems && hasPreorderItems;
  return (
    <Card
      className={classNames(
        "max-h-full overflow-y-auto p-4 sm:max-w-[24rem]",
        className,
      )}
      as={"ul"}
    >
      {cart?.items
        .filter((item) => !isFreightItem(item))
        .map((item) => {
          const {
            name,
            images,
            variant: {
              sku,
              product: { path },
              attributes,
              compareAtPrice,
              name: variantName,
              price: variantPrice,
            },
            quantity,
            price: itemPrice,
          } = item;
          const isDigital = !!attributes?.Innbinding && isItemDigital(item);
          const formattedReleaseDate = item.releaseDate
            ? formatPublicationDate(item.releaseDate)
            : null;

          const addbuttonIsDisabled = isDigital;
          const subjectToVAT = variantPrice?.net
            ? variantPrice.net !== variantPrice.gross
            : null;
          return (
            <li
              key={sku}
              className={classNames(
                "border-b py-4 first-of-type:pt-0 last-of-type:border-none last-of-type:pb-0",
                { "animate-pulse": path === "loading" },
              )}
            >
              <article className="grid grid-cols-[6rem_1fr] items-center gap-4">
                <Canvas className="w-full p-3">
                  {images?.[0]?.url ? (
                    <Image
                      src={images?.[0]?.url || ""}
                      height={200}
                      width={84}
                      alt={`Omslag av ${name}`}
                    />
                  ) : (
                    <BookStencil
                      title=""
                      style={
                        {
                          "--book-width": `84px`,
                        } as React.CSSProperties
                      }
                    />
                  )}
                </Canvas>
                <ProductLink sku={sku}>
                  <p className="mb-1 font-bold [overflow-wrap:anywhere] hyphens-auto">
                    {variantName}
                  </p>
                  <p className="truncate hover:no-underline">
                    {attributes?.Innbinding}
                  </p>
                  {item.releaseDate && (
                    <Tag variant="informational" size="small" className="mt-1">
                      {formattedReleaseDate
                        ? `I salg ${formattedReleaseDate}`
                        : "Forhåndsbestilling"}
                    </Tag>
                  )}
                  {hasConflict && isDigital && (
                    <span className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                      Kun kort
                    </span>
                  )}
                  {hasConflict && preorderSkus.has(sku) && (
                    <span className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                      Kun faktura
                    </span>
                  )}
                </ProductLink>

                <div className="col-span-2 flex items-center justify-between">
                  <div
                    className={classNames(
                      "grid w-fit max-w-[60%] grid-cols-[2rem_auto_2rem] items-center justify-center gap-2 rounded-full border px-2 py-1",
                    )}
                  >
                    {quantity > 1 ? (
                      <button
                        className="rounded-xl px-1 hover:cursor-pointer hover:bg-gray-50 focus:cvr-focus disabled:opacity-25"
                        type="button"
                        onClick={async () => {
                          await decreaseQuantity({ sku });
                        }}
                      >
                        <span className="sr-only">{`Minsk antall til ${quantity - 1}`}</span>
                        <Minus width={24} height={24} />
                      </button>
                    ) : (
                      <button
                        className="rounded-xl px-1 hover:cursor-pointer hover:bg-gray-50 focus:cvr-focus disabled:opacity-25"
                        type="button"
                        onClick={async () => {
                          await removeItem({ sku });
                        }}
                      >
                        <span className="sr-only">{`Fjern ${name}`}</span>
                        <Trash width={24} height={24} />
                      </button>
                    )}
                    <QuantityInput
                      quantity={quantity}
                      onQuantityChange={async (newQuantity) => {
                        await setQuantity({ sku, quantity: newQuantity });
                      }}
                      onRemove={async () => {
                        await removeItem({ sku });
                      }}
                    />
                    <button
                      className={classNames(
                        "rounded-xl px-0.5 hover:cursor-pointer hover:bg-gray-50 focus:cvr-focus",
                        { "opacity-35": addbuttonIsDisabled },
                      )}
                      type="button"
                      disabled={addbuttonIsDisabled}
                      onClick={async () => {
                        await increaseQuantity({ sku });
                      }}
                    >
                      <span className="sr-only">{`Øk antall til ${quantity + 1}`}</span>
                      <Plus width={24} height={24} />
                    </button>
                  </div>
                  <div className="flex flex-wrap justify-end text-right">
                    <Price
                      variant="body-base"
                      className={classNames(
                        "flex-wrap justify-end gap-2 text-lg font-normal",
                      )}
                      isPending={isPending}
                      {...getCartItemPrice({
                        subjectToVAT: subjectToVAT,
                        net: variantPrice?.net ?? null,
                        compareAt:
                          compareAtPrice?.gross ?? variantPrice?.gross ?? null,
                        quantity,
                        itemLineTotal: isPending
                          ? null
                          : (itemPrice?.net ?? null),
                      })}
                    />
                  </div>
                </div>
              </article>
            </li>
          );
        })}
    </Card>
  );
}

function QuantityInput({
  quantity,
  onQuantityChange,
  onRemove,
}: {
  quantity: number;
  onQuantityChange: (quantity: number) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [inputValue, setInputValue] = useState(String(quantity));
  const previousQuantity = useRef(quantity);

  if (previousQuantity.current !== quantity) {
    previousQuantity.current = quantity;
    setInputValue(String(quantity));
  }

  const commitValue = async () => {
    const trimmed = inputValue.trim();
    const parsed = Number(trimmed);

    if (
      trimmed === "" ||
      !Number.isFinite(parsed) ||
      !Number.isInteger(parsed) ||
      parsed < 0 ||
      parsed === quantity
    ) {
      setInputValue(String(quantity));
      return;
    }

    if (parsed === 0) {
      await onRemove();
      return;
    }

    await onQuantityChange(parsed);
  };

  return (
    <input
      type="number"
      min={1}
      style={{ width: `${Math.max(2, inputValue.length + 1)}ch` }}
      className="[appearance:textfield] bg-transparent p-0 text-center outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onBlur={commitValue}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commitValue();
        }
        if (e.key === "Escape") {
          setInputValue(String(quantity));
          (e.target as HTMLInputElement).blur();
        }
      }}
      aria-label="Endre antall"
    />
  );
}
